import { Col, Row } from "reactstrap";

//Common
import {
    Chip,
    ConfirmationDialog,
    DataGrid,
    Drawer,
    Snackbar,
} from "../../common/VFX-UI";
import PageHeader from "../../common/PageHeader/PageHeader";

import MoreActionsMenu from "../../common/MoreActionsMenu/MoreActionsMenu";
import { MoreActionButton } from "../../common/Buttons";

//App
import { ClaimFormContainer } from "./ClaimFormContainer";
import useComponentGeneralStyles from "../../styles";
import { FormikTypeaheadOption } from "../../../types";
import ClaimView from "./ClaimView";
import useClaim from "./hooks/useClaim";
import { ClaimProps } from "../../../types/claim";
import SearchContainer from "../../common/Forms/SearchContainer/SearchContainer";

import { SearchProps } from "../../../types";
import { ClaimDto } from "../../../app/model/api/types/dto";
import { SearchDefaultValue } from "../../../store/types";

import { SearchFieldsDefaultValue } from "../../../utility/constants/constants";

const Claim: React.FC<ClaimProps> = (props) => {
    const { data: claims, deleteMutation, activateMutation } = props;
    const generalStyles = useComponentGeneralStyles();

    const {
        state,
        formContext,
        rowEvents,
        actionColumnAnchorEl,
        toggleNewViewHandler,
        toggleViewHandler,
        toggleEditViewHandler,
        toggleDeactivateHandler,
        toggleActivateHandler,
        handleDeactivate,
        handleActivate,
        onShowSuccess,
        onAddSnackbarClose,
        handleActionClick,
        setActionColumnAnchorEl,
        handleActiveToggleChange,
    } = useClaim({
        deleteMutation: deleteMutation,
        activateMutation: activateMutation,
    });

    const openMenu = Boolean(actionColumnAnchorEl);

    const handleOnClaimTypeChange = async (value: FormikTypeaheadOption[]) => {
        let claimTypeId: string | null = null;

        if (value && value?.length > 0) {
            claimTypeId = (value[0] as Record<string, string | number>)[
                "value"
            ] as string;
        }
        await formContext?.setClaimTypeId(claimTypeId);
    };

    const searchProps: SearchProps<ClaimDto> = {
        dropdown: {options: formContext?.claimTypes ?? [], defaultSelected: formContext?.claimTypeId != null ? [formContext.claimTypeId] : [], 
            clearButton: true, name: "claimTypeId", label: "Claim Type", onChange: (value:any) => handleOnClaimTypeChange(value) },
        searchValue: formContext?.searchValue ?? SearchDefaultValue,
        searchFields: { ...SearchFieldsDefaultValue, name: true },
        setSearchValue: formContext?.setSearchValue,
        refetchGrid: formContext?.refetchGrid,
    };

    const columns = [
        {
            dataField: "id",
            text: "ID",
            hidden: true,
        },
        {
            dataField: "name",
            text: "Name",
            sort: true,
        },
        {
            dataField: "description",
            text: "Description",
            sort: true,
        },
        {
            dataField: "claimType",
            text: "Claim Type",
            sort: true,
        },
        {
            dataField: "isActive",
            text: "Status",
            sort: false,
            formatter: (cell: boolean) => {
                return <Chip label={cell ? "Active" : "Inactive"} />;
            },
        },
        {
            dataField: "action",
            text: "",
            style: { width: "25px" },
            isDummyField: true,
            formatter: () => {
                return (
                    <MoreActionButton handleActionClick={(e) => handleActionClick(e)} />
                );
            },
        },
    ];

    return (
        <>
            <PageHeader>Claim</PageHeader>
            <div style={generalStyles.searchContainer}>
                <Row style={generalStyles.width100}>
                    <Col sm={12}>
                        <SearchContainer
                            searchProps={searchProps}
                            activeToggle={formContext?.activeToggle ?? true}
                            handleActiveToggleChange={handleActiveToggleChange}
                            toggleNewViewHandler={
                                formContext?.claimTypeId !== null
                                    ? toggleNewViewHandler
                                    : undefined
                            }
                        />
                    </Col>
                </Row>
            </div>

            <Drawer show={state.toggleNewForm}>
                {state.toggleNewForm && (
                    <ClaimFormContainer
                        onClose={toggleNewViewHandler}
                        onShowSuccess={onShowSuccess}
                    />
                )}
            </Drawer>

            <Drawer show={state.toggleEditForm}>
                {state.toggleEditForm && formContext?.data && (
                    <ClaimFormContainer
                        onClose={toggleEditViewHandler}
                        onShowSuccess={onShowSuccess}
                    />
                )}
            </Drawer>

            <Drawer show={state.toggleView}>
                {state.toggleView && formContext?.data && (
                    <ClaimView
                        onClose={toggleViewHandler}
                        editAction={() => {
                            toggleEditViewHandler();
                        }}
                        toggleDeactivateHandler={toggleDeactivateHandler}
                        toggleActivateHandler={toggleActivateHandler}
                    />
                )}
            </Drawer>

            <DataGrid
                columns={columns}
                data={claims ?? []}
                keyField="id"
                rowEvents={rowEvents}
                totalSize={formContext?.paging?.totalSize}
                remote={true}
                pageSize={formContext?.pageSize}
                page={formContext?.paging?.currentPage}
                handlePageChange={async (newPage, pageSize ,sortField, sortOrder) => {
                    if (formContext?.setCurrentPage && newPage) {
                        await formContext?.setCurrentPage(newPage);
                    }
                    if (formContext?.setCurrentPageSize && pageSize) {
                        await formContext?.setCurrentPageSize(pageSize);
                    }
                    if (formContext?.setSortBy && sortField && sortOrder) {
                        await formContext?.setSortBy({
                            field: sortField,
                            direction: sortOrder === "asc" ? 1 : -1,
                        });
                    }
                    formContext?.refetchGrid();
                }}
            />

            {actionColumnAnchorEl && (
                <MoreActionsMenu
                    isOpen={openMenu}
                    target={actionColumnAnchorEl}
                    toggle={() => {
                        setActionColumnAnchorEl(null);
                    }}
                    onViewClick={() => {
                        toggleViewHandler();
                        setActionColumnAnchorEl(null);
                    }}
                />
            )}

            <ConfirmationDialog
                open={state.isDeactivateDialogOpen}
                onClose={toggleDeactivateHandler}
                onConfirm={handleDeactivate}
                contentTitle={`Deactivate ${formContext?.data?.name}`}
                contentText={"Are you sure you want to continue?"}
                confirmText={"Yes, Continue"}
                closeText={"No, Go Back"}
            ></ConfirmationDialog>

            <ConfirmationDialog
                open={state.isActivateDialogOpen}
                onClose={toggleActivateHandler}
                onConfirm={handleActivate}
                contentTitle={`Activate ${formContext?.data?.name}`}
                contentText={"Are you sure you want to continue?"}
                confirmText={"Yes, Continue"}
                closeText={"No, Go Back"}
            />

            {state.showSuccessSnackbar && (
                <Snackbar
                    open={state.showSuccessSnackbar}
                    message={state.successMessage}
                    handleClose={onAddSnackbarClose}
                    severity="success"
                />
            )}
        </>
    );
};

export default Claim;
